import { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { AiOrb } from '@forwardflow/features/campaign-flow/components/ui/AiOrb'
import { FlowIcon } from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { ITranscriptLine } from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'

/* Call screens for "Try it as a lead". The user answers, declines, talks and hangs up. */

const CallHeader = ({
   name,
   company,
   hue,
   status,
   ringing,
   talking,
}: {
   name: string
   company: string
   hue: number
   status: string
   ringing?: boolean
   talking?: boolean
}) => (
   <div className="flex flex-col items-center gap-1">
      <div className={cn('relative', ringing && 'cf-ring')}>
         <AiOrb hue={hue} size={ringing ? 92 : 60} talking={talking} />
      </div>
      <div
         className={cn(
            'font-medium mt-2 tracking-[-.01em]',
            ringing ? 'text-[26px]' : 'text-[20px]'
         )}
      >
         {name}
      </div>
      <div className="text-[14px] text-white/70">
         {company ? `${company} · ` : ''}
         {status}
      </div>
   </div>
)

const RoundButton = ({
   label,
   onClick,
   tone,
   icon,
   testId,
}: {
   label: string
   onClick: () => void
   tone: 'accept' | 'decline' | 'neutral'
   icon: 'phone' | 'x' | 'pause'
   testId?: string
}) => (
   <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2 text-[13px] text-white/90"
      data-testid={testId}
   >
      <span
         className={cn(
            'w-[72px] h-[72px] rounded-full flex items-center justify-center',
            tone === 'accept'
               ? 'bg-[#34c759]'
               : tone === 'decline'
                 ? 'bg-[#ff3b30]'
                 : 'bg-white/[0.16]'
         )}
      >
         <FlowIcon
            name={icon}
            size={30}
            className={cn('shrink-0', tone === 'decline' && 'rotate-[135deg]')}
         />
      </span>
      {label}
   </button>
)

export const IncomingCall = ({
   name,
   company,
   hue,
   onAccept,
   onDecline,
}: {
   name: string
   company: string
   hue: number
   onAccept: () => void
   onDecline: () => void
}) => (
   <div
      className="flex-1 flex flex-col px-6 pt-10 pb-10"
      style={{
         background:
            'linear-gradient(180deg,rgba(144,97,218,.35),rgba(10,10,16,.6) 60%)',
      }}
      data-testid="cf-phone-ringing"
   >
      <CallHeader
         name={name}
         company={company}
         hue={hue}
         status="calling you"
         ringing
      />
      <div className="mt-auto mb-6 flex justify-around text-[13px] text-white/80">
         <span className="flex flex-col items-center gap-1.5">
            <span className="w-9 h-9 rounded-full bg-white/[0.12] flex items-center justify-center">
               <FlowIcon name="clock" size={16} />
            </span>
            Remind Me
         </span>
         <span className="flex flex-col items-center gap-1.5">
            <span className="w-9 h-9 rounded-full bg-white/[0.12] flex items-center justify-center">
               <FlowIcon name="chat" size={16} />
            </span>
            Message
         </span>
      </div>
      <div className="flex justify-around">
         <RoundButton
            label="Decline"
            tone="decline"
            icon="phone"
            onClick={onDecline}
            testId="cf-call-decline"
         />
         <RoundButton
            label="Accept"
            tone="accept"
            icon="phone"
            onClick={onAccept}
            testId="cf-call-accept"
         />
      </div>
   </div>
)

export const LiveCall = ({
   name,
   company,
   hue,
   lines,
   ended,
   thinking,
   quickReplies,
   onSay,
   onHangUp,
   mic,
   note,
}: {
   name: string
   company: string
   hue: number
   lines: ITranscriptLine[]
   ended: boolean
   thinking: boolean
   quickReplies: string[]
   onSay: (text: string) => void
   onHangUp: () => void
   /** A real voice call: the lead answers through the microphone, not the keyboard. */
   mic?: 'connecting' | 'live'
   /** Why the call is typed rather than spoken, when it is. */
   note?: string
}) => {
   const [draft, setDraft] = useState('')
   const ref = useRef<HTMLDivElement | null>(null)
   useEffect(() => {
      ref.current?.scrollTo({
         top: ref.current.scrollHeight,
         behavior: 'smooth',
      })
   }, [lines.length, thinking])
   const talking =
      !ended && !thinking && lines[lines.length - 1]?.from === 'assistant'
   const send = (t: string) => {
      onSay(t)
      setDraft('')
   }
   return (
      <div
         className="flex-1 min-h-0 flex flex-col px-4 pt-3 pb-5"
         style={{
            background:
               'linear-gradient(180deg,rgba(144,97,218,.3),rgba(10,10,16,.7) 45%)',
         }}
         data-testid="cf-phone-call"
      >
         <CallHeader
            name={name}
            company={company}
            hue={hue}
            status={ended ? 'call ended' : 'on the call'}
            talking={talking}
         />
         <div
            ref={ref}
            className="flex-1 min-h-0 flex flex-col gap-1.5 mt-2.5 overflow-y-auto cf-scroll"
         >
            <div className="mt-auto" />
            {lines.map((l, i) => (
               <div
                  key={i}
                  className={cn(
                     'cf-bubble-in cf-ios-bubble',
                     l.from === 'lead' ? 'is-me' : 'is-them'
                  )}
               >
                  {l.text}
               </div>
            ))}
            {thinking && (
               <div
                  className="self-start rounded-[18px] rounded-bl-[6px] bg-[#26252a] px-3.5 py-2.5 flex gap-1"
                  aria-label="Speaking"
               >
                  {[0, 1, 2].map((d) => (
                     <i
                        key={d}
                        className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"
                        style={{ animationDelay: `${d * 150}ms` }}
                     />
                  ))}
               </div>
            )}
         </div>
         {note && !ended && (
            <p
               className="text-center text-[11px] leading-4 text-amber-200/80 pt-2"
               data-testid="cf-call-note"
            >
               {note}
            </p>
         )}
         {ended ? (
            <div className="text-center text-[15px] text-white/70 pt-3">
               Call Ended
            </div>
         ) : mic ? (
            <div
               className="flex items-center gap-3 pt-3"
               data-testid="cf-call-mic"
            >
               <span
                  className={cn(
                     'flex-1 h-10 rounded-full border border-white/20 bg-white/[0.06] flex items-center justify-center gap-2 text-[13px] text-white/80',
                     mic === 'live' && 'border-[#0a84ff]/60'
                  )}
               >
                  <span
                     className={cn(
                        'w-2 h-2 rounded-full',
                        mic === 'live'
                           ? 'bg-[#30d158] animate-pulse'
                           : 'bg-white/40'
                     )}
                  />
                  {mic === 'live' ? 'Talk, it hears you' : 'Connecting…'}
               </span>
               <button
                  type="button"
                  onClick={onHangUp}
                  aria-label="Hang up"
                  className="w-12 h-12 rounded-full bg-[#ff3b30] flex items-center justify-center shrink-0"
                  data-testid="cf-call-hangup"
               >
                  <FlowIcon
                     name="phone"
                     size={22}
                     className="shrink-0 rotate-[135deg]"
                  />
               </button>
            </div>
         ) : (
            <>
               <div className="flex gap-1 overflow-x-auto cf-scroll pt-2 pb-1">
                  {quickReplies.map((q) => (
                     <button
                        key={q}
                        type="button"
                        disabled={thinking}
                        onClick={() => send(q)}
                        className="shrink-0 h-8 px-3 rounded-full bg-white/[0.12] text-[13px] text-white/90 hover:bg-white/20 disabled:opacity-40"
                        data-testid={`cf-quick-${q.split(' ')[0].toLowerCase()}`}
                     >
                        {q}
                     </button>
                  ))}
               </div>
               <div className="flex items-center gap-2 pt-1 w-full min-w-0">
                  <form
                     className="flex-1 min-w-0 h-10 rounded-full border border-white/20 bg-white/[0.06] flex items-center pl-3.5 pr-1"
                     onSubmit={(e) => {
                        e.preventDefault()
                        send(draft)
                     }}
                  >
                     <input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Answer as the lead"
                        className="flex-1 min-w-0 bg-transparent text-[14px] outline-none placeholder:text-white/40"
                        data-testid="cf-call-input"
                     />
                     <button
                        type="submit"
                        disabled={!draft.trim() || thinking}
                        className="w-8 h-8 rounded-full bg-[#0a84ff] disabled:opacity-40 flex items-center justify-center"
                        aria-label="Say it"
                     >
                        <FlowIcon
                           name="arrowRight"
                           size={14}
                           className="shrink-0 -rotate-90"
                        />
                     </button>
                  </form>
                  <button
                     type="button"
                     onClick={onHangUp}
                     aria-label="Hang up"
                     className="w-12 h-12 rounded-full bg-[#ff3b30] flex items-center justify-center shrink-0"
                     data-testid="cf-call-hangup"
                  >
                     <FlowIcon
                        name="phone"
                        size={22}
                        className="shrink-0 rotate-[135deg]"
                     />
                  </button>
               </div>
            </>
         )}
      </div>
   )
}

export const MissedCall = ({
   name,
   company,
   when,
}: {
   name: string
   company: string
   when: string
}) => (
   <div
      className="flex-1 flex flex-col items-center justify-center gap-2 px-6 text-center bg-black"
      data-testid="cf-phone-missed"
   >
      <span className="w-14 h-14 rounded-full bg-[#ff3b30]/15 text-[#ff453a] flex items-center justify-center">
         <FlowIcon
            name="phone"
            size={24}
            className="shrink-0 rotate-[135deg]"
         />
      </span>
      <div className="text-[17px] font-semibold">Missed Call</div>
      <div className="text-[14px] text-white/60">
         {name}
         {company ? ` · ${company}` : ''}
         <br />
         {when}
      </div>
      <div className="text-[13px] text-white/45 mt-2">
         This call leaves no voicemail. Turn one on in Timing.
      </div>
   </div>
)
